import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import {
  formatTransferLines,
  trialUploadRows,
  uploadPercent,
} from '../../util/uploadProgress';
import { getSessionTransfer, subscribeSessionTransfer } from '../../util/sessionTransfer';

function stageCount(line) {
  return line.pending ? '…' : `${line.done}/${line.total}`;
}

function noticeTitle(lines) {
  const active = lines.find((line) => !line.pending && line.total > 0 && line.done < line.total);
  if (!active) return { title: 'Sending session', activeKey: null };
  if (active.key === 'cameraDownload') {
    return { title: 'Downloading from cameras', activeKey: active.key };
  }
  return { title: 'Uploading to server', activeKey: active.key };
}

function uploadRank(trial) {
  const cameras = Number(trial.cameraDone) || 0;
  const evowalk = trial.evowalkDone == null ? 0 : (Number(trial.evowalkDone) || 0);
  return cameras + evowalk;
}

function rankTrials(trials) {
  return [...trials].sort((a, b) => {
    const diff = uploadRank(b) - uploadRank(a);
    if (diff) return diff;
    return String(a.id).localeCompare(String(b.id));
  });
}

export default function SessionTransferNotice({ progress: progressProp }) {
  const [liveProgress, setLiveProgress] = useState(() => getSessionTransfer().progress);
  const [minimized, setMinimized] = useState(false);
  const [closed, setClosed] = useState(false);
  const [trialsExpanded, setTrialsExpanded] = useState(false);

  useEffect(() => {
    if (progressProp !== undefined) return undefined;
    return subscribeSessionTransfer((snap) => setLiveProgress(snap.progress));
  }, [progressProp]);

  const progress = progressProp !== undefined ? progressProp : liveProgress;

  useEffect(() => {
    if (!progress) return undefined;
    setMinimized(false);
    setClosed(false);
    setTrialsExpanded(false);
    return undefined;
  }, [progress ? 1 : 0]);

  if (!progress) return null;

  const lines = formatTransferLines(progress);
  const ranked = rankTrials(trialUploadRows(progress));
  const shown = trialsExpanded ? ranked : ranked.slice(0, 2);
  const { title, activeKey } = noticeTitle(lines);
  const percent = uploadPercent(progress);
  const trialLabel = progress.trialCount === 1 ? '1 trial' : `${progress.trialCount} trials`;

  let body;
  if (closed) {
    body = (
      <aside
        className="session-transfer-notice session-transfer-notice--launcher"
        role="status"
        aria-live="polite"
        aria-label="Session transfer"
      >
        <button type="button" className="session-transfer-notice__restore" onClick={() => setClosed(false)}>
          {`${title} ${percent}%`}
        </button>
      </aside>
    );
  } else if (minimized) {
    body = (
      <aside
        className="session-transfer-notice session-transfer-notice--min"
        role="status"
        aria-live="polite"
        aria-label="Session transfer"
      >
        <button type="button" className="session-transfer-notice__restore" aria-label="Expand session transfer" onClick={() => setMinimized(false)}>
          {`${title} ${percent}%`}
        </button>
        <button type="button" className="session-transfer-notice__icon-btn" aria-label="Close session transfer" onClick={() => setClosed(true)}>
          <Icon icon="mdi:close" />
        </button>
      </aside>
    );
  } else {
    body = (
      <aside className="session-transfer-notice" role="status" aria-live="polite" aria-label="Session transfer">
        <div className="session-transfer-notice__header">
          <strong>{title}</strong>
          <span className="session-transfer-notice__percent">{`${percent}%`}</span>
          <div className="session-transfer-notice__actions">
            <button type="button" className="session-transfer-notice__icon-btn" aria-label="Minimize session transfer" onClick={() => setMinimized(true)}>
              <Icon icon="mdi:minus" />
            </button>
            <button type="button" className="session-transfer-notice__icon-btn" aria-label="Close session transfer" onClick={() => setClosed(true)}>
              <Icon icon="mdi:close" />
            </button>
          </div>
        </div>
        <div className="session-transfer-notice__meta">{trialLabel}</div>
        <div className="session-transfer-notice__bar" aria-hidden="true">
          <div className="session-transfer-notice__bar-fill" style={{ width: `${percent}%` }} />
        </div>
        <ul className="session-transfer-notice__stages">
          {lines.map((line) => (
            <li key={line.key} className={line.key === activeKey ? 'is-active' : undefined}>
              <span>{line.label}</span>
              {' '}
              <span>{stageCount(line)}</span>
            </li>
          ))}
        </ul>
        {shown.length > 0 && (
          <ul className="session-transfer-notice__trials">
            {shown.map((trial) => (
              <li key={trial.id}>
                <span>{`Trial ${trial.id} ·`}</span>
                {' '}
                <span>
                  {`cameras ${trial.cameraDone}/${trial.cameraTotal}`}
                  {trial.evowalkDone == null ? '' : ` · EvoWalk ${trial.evowalkDone}/${trial.evowalkTotal}`}
                </span>
              </li>
            ))}
          </ul>
        )}
        {ranked.length > 2 && (
          <button
            type="button"
            className="session-transfer-notice__expand"
            aria-expanded={trialsExpanded}
            onClick={() => setTrialsExpanded((open) => !open)}
          >
            <Icon icon={trialsExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
            {trialsExpanded ? 'Show fewer trials' : 'Show all trials'}
          </button>
        )}
      </aside>
    );
  }

  return createPortal(body, document.body);
}
